import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useSnapActions,
  useSnapAnchorElement,
  useSnapMode,
  useSnapVisitDetails,
} from 'views/EMR/stores/snap';
import usePopulateRecord from 'views/EMR/components/Snap/usePopulateRecord';
import defer from 'lodash/defer';
import isNil from 'lodash/isNil';
import SnapPopover, {
 formatDurationToTime,
} from 'views/EMR/components/Snap/SnapPopover';
import MinimizedSnap from 'views/EMR/components/Snap/MinimizedSnap';
import {
  Section,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import {
  useStartSnapNoteGeneration,
} from 'views/EMR/components/Snap/hooks';
import {
  SnapStates,
} from 'dtos/snapStates';
import {
  useSilenceDetection,
} from 'views/EMR/components/Snap/useSilenceDetection';
import {
 datadogLogs,
} from '@datadog/browser-logs';

const readableStates: Partial<Record<SnapStates, string>> = Object.freeze({
  [SnapStates.Transcribing]: 'Transcribing...',
  [SnapStates.TranscriptionSucceeded]: 'Generating Note...',
  [SnapStates.GeneratingNote]: 'Generating Note...',
  [SnapStates.GenerateNoteSucceeded]: 'Generating Note...',
});

export default function SnapContainer() {
  const snapMode = useSnapMode();
  const anchorElement = useSnapAnchorElement();
  const visitDetails = useSnapVisitDetails();
  const { closeSnap, setGeneratedNote, setIsGeneratingNote } = useSnapActions();
  const {
    recordingState,
    startRecording,
    pauseRecording,
    resumeRecording,
    endRecording,
    recordedBlob,
    clearRecordingStates,
    stream,
    duration,
    error,
  } = usePopulateRecord();

  const [isProcessing, setIsProcessing] = useState(false);
  useEffect(() => {
    if (snapMode === 'closed') {
      endRecording();
      defer(clearRecordingStates);
      closeSnap();
      setIsProcessing(false);
    }
  }, [snapMode]);

  const onNoteGenerated = (note: Section[]) => {
    datadogLogs.logger.info(`[Snap] Note generated - ${formatDurationToTime(duration ?? 0)}`);
    setGeneratedNote(note);
    setIsProcessing(false);
  };

  const onNoteGenerationFailed = (errorMessage: string) => {
    datadogLogs.logger.error(
      `[Snap] Note generation failed - ${formatDurationToTime(duration ?? 0)}`,
      { errorMessage },
    );
    setIsProcessing(false);
  };

  const {
    startSnapNoteGeneration,
    snapProgressState,
  } = useStartSnapNoteGeneration({
    onNoteGenerated,
    onNoteGenerationFailed,
  });

  const currentState = useMemo(() => {
    let text = readableStates[snapProgressState?.state as SnapStates] ?? 'Processing audio...';
    if (!isProcessing) {
      text = 'Generate New Note';
    }
    return text;
  }, [snapProgressState?.state, isProcessing]);

  const onSubmit = useCallback(async () => {
    if (!isNil(recordedBlob)) {
      try {
        setIsProcessing(true);
        const ext = recordedBlob.type.includes('webm') ? 'webm' : 'bin';
        const fileName = `snap-${Date.now()}.${ext}`;
        await startSnapNoteGeneration(
          visitDetails?.encounterId ?? 0,
          recordedBlob,
          fileName,
          duration,
        );
      } catch (error) {
        setIsProcessing(false);
      }
    }
  }, [recordedBlob]);

  useEffect(() => {
    setIsGeneratingNote(isProcessing);
  }, [isProcessing]);

  const {
    kind: speechStatus,
  } = useSilenceDetection({
    stream,
    enabled: recordingState === 'recording',
  });

  return snapMode === 'closed' ? null : (
    <>
      {!isNil(anchorElement) && (
        <SnapPopover
          currentState={currentState}
          recordingState={recordingState}
          startRecording={startRecording}
          pauseRecording={pauseRecording}
          resumeRecording={resumeRecording}
          endRecording={endRecording}
          recordedBlob={recordedBlob}
          onDelete={clearRecordingStates}
          stream={stream}
          onSubmit={onSubmit}
          isGeneratingNote={isProcessing}
          duration={duration}
          error={error}
          showSilentDetection={speechStatus === 'no-input'}
        />
      )}
      {snapMode === 'withMinimizedControls' && (
        <MinimizedSnap recordingState={recordingState} />
      )}
    </>
  );
}
